import {useEmojisQuery} from '@github-ui/emoji-autocomplete/use-emojis-query'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {getBaseFetchHeaders} from '@github-ui/fetch-headers'
import {IncludeFragment} from '@github-ui/include-fragment-react'
import {InlineAutocomplete} from '@github-ui/inline-autocomplete'
import {useAutocompleteTriggersAndSuggestions} from '@github-ui/inline-autocomplete/hooks/use-autocomplete-triggers-and-suggestions'
import {useEmojiSuggestions} from '@github-ui/markdown-editor/suggestions/use-emoji-suggestions'
import {useMentionSuggestions} from '@github-ui/markdown-editor/suggestions/use-mention-suggestions'
import {testIdProps} from '@github-ui/test-id-props'
import {getUtf8StringLength} from '@github-ui/text'
import {verifiedFetch} from '@github-ui/verified-fetch'
import {AlertIcon} from '@primer/octicons-react'
import {Checkbox, FormControl, Spinner, TextInput} from '@primer/react'
import {Dialog} from '@primer/react/experimental'
import {type FormEvent, forwardRef, useCallback, useEffect, useId, useMemo, useRef, useState} from 'react'

import type {EmojiAttributes} from './Emoji'
import {useMentionsQuery} from './hooks/use-mentions-query'

const SUGGESTION_TYPES = {
  EMOJI: 'emoji',
  MENTIONS: 'mentions',
}

const MESSAGE_MAX_LENGTH = 80

const emptyArray: [] = [] // constant reference to avoid re-running effects

const EditStatusAutoComplete = ({
  defaultValue,
  onCharacterCountChange,
  organization,
  tone,
}: {
  defaultValue: string
  onCharacterCountChange: (value: number) => void
  tone: number
  organization?: {
    globalRelayId?: string
  }
}) => {
  const [suggesting, setSuggesting] = useState<string | undefined>(undefined)
  const emojiQuery = useEmojisQuery(suggesting === SUGGESTION_TYPES.EMOJI)
  const mentionSuggestions = useMentionsQuery(suggesting === SUGGESTION_TYPES.MENTIONS, organization)
  const {calculateSuggestions: calculateEmojiSuggestions, trigger: emojiTrigger} = useEmojiSuggestions(
    emojiQuery.isPending ? 'loading' : emojiQuery.data ?? emptyArray,
    {tone},
  )
  const {trigger: mentionsTrigger, calculateSuggestions: calculateMentionSuggestions} = useMentionSuggestions(
    mentionSuggestions.isPending ? 'loading' : mentionSuggestions.data ?? emptyArray,
  )

  const triggersAndSuggestions = useMemo(
    () => [
      {
        trigger: emojiTrigger,
        suggestionsCalculator: calculateEmojiSuggestions,
      },
      {trigger: mentionsTrigger, suggestionsCalculator: calculateMentionSuggestions},
    ],
    [emojiTrigger, calculateEmojiSuggestions, mentionsTrigger, calculateMentionSuggestions],
  )

  const {triggers, suggestions, setSuggestionEvent} = useAutocompleteTriggersAndSuggestions(triggersAndSuggestions)

  const inputRef = useRef<HTMLInputElement>(null)
  const onStatusChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const valueLength = getUtf8StringLength(e.target.value)
      onCharacterCountChange(valueLength)
    },
    [onCharacterCountChange],
  )

  return (
    <InlineAutocomplete
      fullWidth
      triggers={triggers}
      suggestions={suggestions}
      onShowSuggestions={event => {
        setSuggestionEvent(event)
        switch (event.trigger.triggerChar) {
          case emojiTrigger.triggerChar:
            setSuggesting(SUGGESTION_TYPES.EMOJI)
            break
          case mentionsTrigger.triggerChar:
            setSuggesting(SUGGESTION_TYPES.MENTIONS)
            break
          default:
            setSuggesting(undefined)
            break
        }
      }}
      onHideSuggestions={() => {
        setSuggestionEvent(null)
        setSuggesting(undefined)
      }}
    >
      <TextInput
        block
        name="message"
        defaultValue={defaultValue}
        maxLength={MESSAGE_MAX_LENGTH}
        onChange={onStatusChange}
        ref={inputRef}
        data-testid="user-status-message-input"
      />
    </InlineAutocomplete>
  )
}

export interface UserStatus {
  message?: string
  messageHtml?: string
  emojiAttributes?: EmojiAttributes
  limitedAvailability?: boolean
  organization?: {
    globalRelayId?: string
  }
}

export interface UserPreferences {
  tone?: number
}

async function saveUserStatus(body: FormData) {
  // put method is required for the endpoint to accept the request
  body.set('_method', 'put')
  const response = await verifiedFetch('/users/status', {
    method: 'POST',
    headers: {...getBaseFetchHeaders(), Accept: 'application/json'},
    body,
  })
  return response.json() as Promise<UserStatus>
}

export const UserStatusDialog = forwardRef<
  HTMLDivElement,
  {
    onClose: (statusResponse?: Promise<UserStatus> | string) => void
    userStatusData?: UserStatus
    userPreferences?: UserPreferences
  }
>(({onClose, userPreferences, userStatusData}, ref) => {
  const [fragmentLoaded, setFragmentLoaded] = useState(false)

  const [characterCount, setCharacterCount] = useState<number>(() => getUtf8StringLength(userStatusData?.message ?? ''))
  const globalNavReactEditStatusDialog = isFeatureEnabled('global_nav_react_edit_status_dialog')
  const fragmentRef = useRef<HTMLElement>(null)
  const formId = useId()

  const onFormSubmit = useCallback(
    (e: FormEvent) => {
      e.preventDefault()
      const data = new FormData(e.target as HTMLFormElement)
      onClose(saveUserStatus(data))
    },
    [onClose],
  )

  const onClearStatus = useCallback(() => {
    // Fire and forget an empty request to clear the status
    void saveUserStatus(new FormData())

    // Immediately close with cleared status
    onClose(Promise.resolve({}))
  }, [onClose])

  useEffect(() => {
    const fragment = fragmentRef.current
    if (!fragment) {
      return
    }

    function loaded() {
      setFragmentLoaded(true)
    }

    fragment.addEventListener('load', loaded)
    return () => {
      fragment.removeEventListener('load', loaded)
    }
  }, [fragmentRef])

  return (
    <Dialog
      width="large"
      title="Edit status"
      onClose={onClose}
      footerButtons={[
        {
          buttonType: 'normal',
          content: 'Clear status',
          onClick: onClearStatus,
        },
        {
          buttonType: 'primary',
          type: 'submit',
          content: 'Set status',
          form: formId,
          disabled: !globalNavReactEditStatusDialog && !fragmentLoaded,
        },
      ]}
      ref={ref}
    >
      <form id={formId} onSubmit={onFormSubmit} className="user-status-dialog-fragment js-user-status-container">
        {globalNavReactEditStatusDialog ? (
          <>
            <FormControl className="mb-2">
              <FormControl.Label>What&apos;s happening?</FormControl.Label>
              <EditStatusAutoComplete
                defaultValue={userStatusData?.message || ''}
                organization={userStatusData?.organization}
                onCharacterCountChange={setCharacterCount}
                tone={userPreferences?.tone || 0}
              />
              <FormControl.Caption>
                {characterCount}/{MESSAGE_MAX_LENGTH}
              </FormControl.Caption>
            </FormControl>
            <FormControl className="border-bottom pb-3 mb-2">
              <FormControl.Label>Busy</FormControl.Label>
              <Checkbox
                value={'1'}
                name={'limited_availability'}
                defaultChecked={userStatusData?.limitedAvailability || false}
                data-testid="user-status-limited-availability-checkbox"
              />
              <FormControl.Caption>
                When others mention you, assign you, or request your review, GitHub will let them know that you have
                limited availability.
              </FormControl.Caption>
            </FormControl>
          </>
        ) : (
          <IncludeFragment
            src={`/users/status`}
            accept="text/fragment+html"
            ref={fragmentRef}
            {...testIdProps('user-status-dialog-include-fragment')}
          >
            <p className="text-center mt-3" data-hide-on-error>
              <Spinner />
            </p>
            <p className="flash flash-error mb-0 mt-2" data-show-on-error hidden>
              <AlertIcon />
              Sorry, something went wrong and we were not able to fetch the user settings form
            </p>
          </IncludeFragment>
        )}
      </form>
    </Dialog>
  )
})

UserStatusDialog.displayName = 'UserStatusDialog'

try{ EditStatusAutoComplete.displayName ||= 'EditStatusAutoComplete' } catch {}